/**
 * Resumes interrupted 3D-model uploads.
 *
 * Triggers a resume run on THREE moments (same routine):
 *   1. App load — once the logged-in user/org is known (covers reload / reopen).
 *   2. Network regained — `window.online` (covers an in-tab connection drop, no reload needed).
 *   3. A fresh upload being persisted — `RESUMABLE_UPLOAD_ADDED_EVENT` (covers an in-tab upload
 *      that stalls without a reload or a network blip).
 *
 * On top of those, a safety-net poll re-checks while work is still pending — but ONLY while the
 * IDB store is non-empty. The moment everything is done it stops, so an idle app never polls.
 *
 * Scoped strictly to the current user + organization. See docs/resumable-3d-model-upload.md.
 */

import { useEffect, useRef } from 'react';
import {
  resumeAllPendingUploads,
  getResumableUploads,
  RESUMABLE_UPLOAD_ADDED_EVENT,
  RESUME_OUTCOME,
} from '../helpers/resumableModelUpload';
import { dispatcher } from '../helpers/projectHelper';
import { clearCacheFromAPI } from '../helpers/jsHelper';
import { paginatedProductActions } from '../redux/slicers/admin/paginatedProductSlicer';
import { patchUploadedFiles } from '../redux/slicers/appSlicer';
import { FILE_STATUS } from '../components/UploadFileProgressModal';

const ONLINE_DEBOUNCE_MS = 1500;
const RESUME_POLL_MS = 10000; // retry cadence WHILE pending work exists; not a perpetual timer

/**
 * Tell the upload progress card how each record settled, so a row recovered in the background stops
 * showing the failed state its original attempt left behind.
 *
 * Only SETTLED outcomes are reported. A transient `retry` is deliberately silent: the row is already
 * showing "Retrying…", and re-asserting it every 10s would undo the card's own give-up timer.
 * Matching is by `tempId`, which is the record's client uuid — the same key the card is keyed by.
 */
function reportToUploadCard(results = []) {
  const patches = results
    .filter((result) => result?.tempId != null && result.outcome !== RESUME_OUTCOME.RETRY)
    .map((result) =>
      result.outcome === RESUME_OUTCOME.DONE
        ? { id: result.tempId, status: FILE_STATUS.DONE, progress: 100, errorMessage: '' }
        : { id: result.tempId, status: FILE_STATUS.FAILED, errorMessage: 'Upload failed' }
    );
  if (patches.length) dispatcher(patchUploadedFiles(patches));
}

// Toggle to ask the user before resuming. Kept OFF for now (auto-resume); pending team confirmation.
// const ASK_BEFORE_RESUME = false;

export function useResumeModelUploads(currentUser) {
  const runningRef = useRef(false);
  const debounceRef = useRef(null);
  const pollRef = useRef(null);

  const orgId = currentUser?.organization;
  const userId = currentUser?.user?.id;

  useEffect(() => {
    if (!orgId) return; // wait until the logged-in org is known

    const stopRetryPoll = () => {
      clearInterval(pollRef.current);
      pollRef.current = null;
    };

    const runResume = async () => {
      if (runningRef.current) return; // don't run twice at once
      // Skip while offline — the existence check can't reach the server, and we must not act on
      // ambiguous reads. The `online` listener re-triggers this once truly reconnected.
      if (navigator?.onLine === false) return;

      runningRef.current = true;
      try {
        // ASK_BEFORE_RESUME path (commented, pending team decision):
        // if (ASK_BEFORE_RESUME) { openInfoDialog({ title: 'Resume interrupted upload?', ... }); return; }

        // Refresh the list silently (in place) — patch only the finished rows so the page never
        // flashes a loader. No-op for rows not in the currently-loaded list.
        const { products, results } = await resumeAllPendingUploads({ orgId, userId });
        if (products.length) {
          // Bust the API cache so any later list/detail refetch serves fresh data, not a stale entry.
          clearCacheFromAPI();
          products.forEach((product) => dispatcher(paginatedProductActions.update(product)));
        }
        reportToUploadCard(results);
      } catch (e) {
        console.warn('useResumeModelUploads: resume run failed', e);
      } finally {
        runningRef.current = false;
      }
    };

    // Start the safety-net poll only if it isn't already running. Each tick stops itself once the
    // store is empty, so we never poll an idle app.
    const startRetryPoll = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(async () => {
        const pending = await getResumableUploads({ orgId, userId });
        if (!pending.length) return stopRetryPoll(); // nothing left -> stop until new work appears
        await runResume();
      }, RESUME_POLL_MS);
    };

    // Run a resume now, then keep the retry poll alive only if something is still waiting.
    const resumeAndSyncPoll = async () => {
      await runResume();
      const pending = await getResumableUploads({ orgId, userId });
      if (pending.length) startRetryPoll();
      else stopRetryPoll();
    };

    resumeAndSyncPoll(); // on app load

    // On network regained (debounced against a flapping connection).
    const onOnline = () => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(resumeAndSyncPoll, ONLINE_DEBOUNCE_MS);
    };
    window.addEventListener('online', onOnline);

    // A freshly persisted upload starts the retry poll immediately (no wait for reload / network blip).
    const onAdded = () => startRetryPoll();
    window.addEventListener(RESUMABLE_UPLOAD_ADDED_EVENT, onAdded);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener(RESUMABLE_UPLOAD_ADDED_EVENT, onAdded);
      clearTimeout(debounceRef.current);
      stopRetryPoll();
    };
  }, [orgId, userId]);
}

export default useResumeModelUploads;
